import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    FiChevronLeft, FiChevronRight, FiChevronDown, FiChevronUp,
    FiFilter, FiToggleLeft, FiToggleRight, FiUsers, FiPackage,
    FiCheckCircle, FiXCircle, FiAlertTriangle, FiRefreshCw,
    FiSearch, FiDownload, FiMoreVertical, FiExternalLink
} from 'react-icons/fi';
import { apiFetch, downloadCsv } from '../utils/api';
import OrgDetailDrawer from '../components/OrgDetailDrawer';

const PLANS = ['free', 'starter', 'team', 'business', 'enterprise'];
const PLAN_COLORS = {
    free:       { bg: 'bg-gray-700', text: 'text-gray-300' },
    starter:    { bg: 'bg-blue-900/50', text: 'text-blue-300' },
    team:       { bg: 'bg-purple-900/50', text: 'text-purple-300' },
    business:   { bg: 'bg-amber-900/50', text: 'text-amber-300' },
    enterprise: { bg: 'bg-emerald-900/50', text: 'text-emerald-300' },
};

const PlanBadge = ({ plan }) => {
    const c = PLAN_COLORS[plan] || { bg: 'bg-gray-700', text: 'text-gray-300' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>{plan}</span>;
};

const RoleBadge = ({ role }) => {
    const c = role === 'owner' ? 'bg-amber-900/50 text-amber-300' :
              role === 'admin' ? 'bg-blue-900/50 text-blue-300' :
              'bg-gray-700 text-gray-400';
    return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${c}`}>{role}</span>;
};

const Organizations = () => {
    const [data,        setData]        = useState(null);
    const [loading,     setLoading]     = useState(true);
    const [page,        setPage]        = useState(1);
    const [planFilter,  setPlanFilter]  = useState('');
    const [statusFilter,setStatusFilter]= useState('');
    const [search,      setSearch]      = useState('');
    const [expanded,    setExpanded]    = useState(null);
    const [detailOrgId, setDetailOrgId] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [notification,  setNotification]  = useState(null);
    const [exporting,   setExporting]   = useState(false);
    const [menuOpen,    setMenuOpen]    = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const notify = (msg, ok = true) => {
        setNotification({ msg, ok });
        setTimeout(() => setNotification(null), 3000);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 15 });
            if (planFilter)    params.set('plan', planFilter);
            if (statusFilter)  params.set('status', statusFilter);
            if (search.trim()) params.set('search', search.trim());
            const d = await apiFetch(`/superadmin/orgs?${params}`);
            setData(d);
        } catch (e) { notify(e.message, false); }
        finally { setLoading(false); }
    }, [page, planFilter, statusFilter, search]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (planFilter)    params.set('plan', planFilter);
            if (statusFilter)  params.set('status', statusFilter);
            if (search.trim()) params.set('search', search.trim());
            await downloadCsv(`/superadmin/export/orgs?${params}`, 'organisations.csv');
        } catch (e) { notify(e.message, false); }
        finally { setExporting(false); }
    };

    useEffect(() => { load(); }, [load]);

    const handleStatus = async (id, newStatus) => {
        setActionLoading(id + '-s');
        try {
            await apiFetch(`/superadmin/orgs/${id}/status`, {
                method: 'PATCH', body: JSON.stringify({ status: newStatus })
            });
            notify(`Organisation ${newStatus === 'active' ? 'réactivée' : 'suspendue'}`);
            load();
        } catch (e) { notify(e.message, false); }
        finally { setActionLoading(null); }
    };

    const handlePlan = async (id, newPlan) => {
        setActionLoading(id + '-p');
        try {
            await apiFetch(`/superadmin/orgs/${id}/plan`, {
                method: 'PATCH', body: JSON.stringify({ plan: newPlan })
            });
            notify(`Plan mis à jour : ${newPlan}`);
            load();
        } catch (e) { notify(e.message, false); }
        finally { setActionLoading(null); }
    };

    return (
        <>
        <div className="p-4 sm:p-6 space-y-6">
            {/* Notif */}
            {notification && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl border shadow-xl text-sm font-medium flex items-center gap-2 ${
                    notification.ok
                        ? 'bg-emerald-900/80 border-emerald-700 text-emerald-200'
                        : 'bg-red-900/80 border-red-700 text-red-200'
                }`}>
                    {notification.ok ? <FiCheckCircle size={15} /> : <FiXCircle size={15} />}
                    {notification.msg}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-y-3">
                <h1 className="text-2xl font-bold text-white">Organisations</h1>
                <div className="relative" ref={menuRef}>
                    <button
                        onClick={() => setMenuOpen(o => !o)}
                        className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 hover:text-white transition-colors"
                        title="Options"
                    >
                        <FiMoreVertical size={16} />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-20 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden min-w-[160px]">
                            <button onClick={() => { handleExport(); setMenuOpen(false); }} disabled={exporting}
                                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50">
                                <FiDownload size={13} className={exporting ? 'animate-bounce' : ''} />
                                Exporter CSV
                            </button>
                            <button onClick={() => { load(); setMenuOpen(false); }} disabled={loading}
                                className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-50">
                                <FiRefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                                Actualiser
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Filtres + Recherche */}
            <div className="flex flex-wrap gap-3 items-center bg-gray-900 border border-gray-800 rounded-xl p-3">
                <FiFilter size={14} className="text-gray-500" />
                <div className="relative">
                    <FiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }}
                        className="bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500 w-44"
                    />
                </div>
                <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500">
                    <option value="">Tous les plans</option>
                    {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                    className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-gray-300 text-sm focus:outline-none focus:border-brand-500">
                    <option value="">Tous les statuts</option>
                    <option value="active">Actifs</option>
                    <option value="suspended">Suspendus</option>
                </select>
                {data && <span className="text-gray-500 text-sm ml-auto">{data.total} organisation{data.total > 1 ? 's' : ''}</span>}
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-16">
                    <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                </div>
            ) : (
                <div className="space-y-2">
                    {data?.orgs?.map(org => (
                        <div key={org._id} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                            <div
                                className="flex items-center gap-4 px-4 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors"
                                onClick={() => setExpanded(expanded === org._id ? null : org._id)}
                            >
                                {/* Statut indicator */}
                                <div className={`w-2 h-2 rounded-full shrink-0 ${org.status === 'active' ? 'bg-emerald-400' : 'bg-red-500'}`} />

                                {/* Infos principales */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={e => { e.stopPropagation(); setDetailOrgId(org._id); }}
                                            className="text-white font-semibold hover:text-brand-400 transition-colors flex items-center gap-1"
                                            title="Voir le détail"
                                        >
                                            {org.name}
                                            <FiExternalLink size={12} className="text-gray-600 hover:text-brand-400" />
                                        </button>
                                        <span className="text-gray-500 text-sm">/{org.slug}</span>
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                                        <PlanBadge plan={org.plan} />
                                        <span className="text-gray-500 text-xs flex items-center gap-1">
                                            <FiUsers size={11} /> {org.memberCount} membre{org.memberCount > 1 ? 's' : ''}
                                        </span>
                                        <span className="text-gray-500 text-xs flex items-center gap-1">
                                            <FiPackage size={11} /> {org.usage?.messages ?? 0} msgs
                                        </span>
                                        <span className="text-gray-600 text-xs">{new Date(org.createdAt).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                                    <select
                                        value={org.plan}
                                        onChange={e => handlePlan(org._id, e.target.value)}
                                        disabled={actionLoading === org._id + '-p'}
                                        className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-gray-300 text-xs focus:outline-none focus:border-brand-500 disabled:opacity-50"
                                    >
                                        {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                    <button
                                        onClick={() => handleStatus(org._id, org.status === 'active' ? 'suspended' : 'active')}
                                        disabled={actionLoading === org._id + '-s'}
                                        title={org.status === 'active' ? 'Suspendre' : 'Réactiver'}
                                        className={`p-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                                            org.status === 'active'
                                                ? 'border-red-800 text-red-400 hover:bg-red-900/30'
                                                : 'border-emerald-800 text-emerald-400 hover:bg-emerald-900/30'
                                        }`}
                                    >
                                        {org.status === 'active' ? <FiToggleLeft size={16} /> : <FiToggleRight size={16} />}
                                    </button>
                                </div>

                                {expanded === org._id ? <FiChevronUp size={16} className="text-gray-500 shrink-0" /> : <FiChevronDown size={16} className="text-gray-500 shrink-0" />}
                            </div>

                            {/* Détails */}
                            {expanded === org._id && (
                                <div className="border-t border-gray-800 p-4 bg-gray-950/50 space-y-4">
                                    {/* Stats */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                        {[
                                            ['Messages', org.usage?.messages ?? 0],
                                            ['Todos', org.usage?.todos ?? 0],
                                            ['Type', org.type],
                                            ['ID', org._id],
                                        ].map(([k, v]) => (
                                            <div key={k} className="bg-gray-900 rounded-lg p-3">
                                                <div className="text-gray-500 text-xs mb-1">{k}</div>
                                                <div className="text-white font-medium font-mono text-xs break-all">{v}</div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Membres */}
                                    <div>
                                        <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2">
                                            Membres ({org.memberCount})
                                        </h4>
                                        {org.members?.length === 0 ? (
                                            <p className="text-gray-600 text-sm">Aucun membre</p>
                                        ) : (
                                            <div className="bg-gray-900 rounded-xl overflow-hidden">
                                                {org.members?.map((m, i) => (
                                                    <div key={m._id} className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-gray-800' : ''}`}>
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className="w-7 h-7 rounded-full bg-brand-600/20 flex items-center justify-center text-brand-400 text-xs font-bold shrink-0">
                                                                {(m.name || '?')[0].toUpperCase()}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="text-white text-sm font-medium truncate">{m.name}</div>
                                                                <div className="text-gray-500 text-xs truncate">{m.email}</div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <RoleBadge role={m.role} />
                                                            {!m.isEmailVerified && <FiAlertTriangle size={13} className="text-amber-400" title="Email non vérifié" />}
                                                            <span className="text-gray-600 text-xs">{new Date(m.joinedAt).toLocaleDateString('fr-FR')}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {data && data.pages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-2">
                    <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}
                        className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 transition-colors">
                        <FiChevronLeft size={16} />
                    </button>
                    <span className="text-gray-400 text-sm">{page} / {data.pages}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={page >= data.pages}
                        className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30 transition-colors">
                        <FiChevronRight size={16} />
                    </button>
                </div>
            )}
        </div>

        {detailOrgId && (
            <OrgDetailDrawer
                orgId={detailOrgId}
                onClose={() => setDetailOrgId(null)}
                onUpdate={() => load()}
            />
        )}
        </>
    );
};

export default Organizations;
